import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderAPI } from '../services/api';
import { Spinner } from '../components/shared/index';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  'Pending':          { color: '#b8860b', bg: '#fffbeb', label: 'Pending'          },
  'Approved':         { color: '#0277bd', bg: '#e3f2fd', label: 'Approved'         },
  'Shipped':          { color: '#5e35b1', bg: '#f3e5f5', label: 'Shipped'          },
  'Delivered':        { color: '#2d6a2d', bg: '#e8f5e9', label: 'Delivered'        },
  'Active Rental':    { color: '#1b5e20', bg: '#e8f5e9', label: 'Active Rental'    },
  'Return Requested': { color: '#b8860b', bg: '#fffbeb', label: 'Return Requested' },
  'Returned':         { color: '#555',    bg: '#f5f5f5', label: 'Returned'         },
  'Completed':        { color: '#555',    bg: '#f5f5f5', label: 'Completed'        },
  'Rejected':         { color: '#b71c1c', bg: '#ffebee', label: 'Rejected'         },
  'Cancelled':        { color: '#b71c1c', bg: '#ffebee', label: 'Cancelled'        },
};

const FILTERS = ['All', 'Active', 'Completed', 'Cancelled'];

function statusMatch(status, filter) {
  if (filter === 'All')       return true;
  if (filter === 'Active')    return ['Pending','Approved','Shipped','Delivered','Active Rental','Return Requested'].includes(status);
  if (filter === 'Completed') return ['Returned','Completed'].includes(status);
  if (filter === 'Cancelled') return ['Rejected','Cancelled'].includes(status);
  return true;
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { color: '#6b7280', bg: '#f9fafb', label: status };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px',
      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}33`,
    }}>
      {cfg.label}
    </span>
  );
}

function OrderRow({ order }) {
  const navigate = useNavigate();
  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const item  = order.items?.[0];
  const extra = (order.items?.length || 1) - 1;
  const firstItem  = order.items?.[0];
  const returnDate = firstItem?.returnDate ? new Date(firstItem.returnDate) : null;
  const today      = new Date(); today.setHours(0,0,0,0);
  const daysLeft   = returnDate ? Math.ceil((returnDate - today) / 86400000) : null;
  const isOverdue  = daysLeft !== null && daysLeft < 0 && order.status === 'Active Rental';
  const isDueSoon  = daysLeft !== null && daysLeft >= 0 && daysLeft <= 2 && order.status === 'Active Rental';

  return (
    <div
      onClick={() => navigate(`/orders/${order._id}`)}
      style={{
        background: 'white', border: '1px solid var(--border)',
        borderLeft: `3px solid ${isOverdue ? '#b71c1c' : isDueSoon ? '#b8860b' : 'transparent'}`,
        cursor: 'pointer', transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.borderLeftColor = isOverdue ? '#b71c1c' : isDueSoon ? '#b8860b' : 'var(--gold)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderLeftColor = isOverdue ? '#b71c1c' : isDueSoon ? '#b8860b' : 'transparent'; }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '10px 18px', background: 'var(--cream-dark)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            ['Order', `#${order.orderId}`],
            ['Placed', fmt(order.createdAt)],
            ['Total', `₹${order.grandTotal?.toLocaleString('en-IN')}`],
            ['Payment', order.payment?.method === 'cod' ? 'Cash on Delivery' : 'Online'],
          ].map(([label, val]) => (
            <div key={label}>
              <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', fontFamily: label === 'Total' ? "'Cormorant Garamond', serif" : "'Josefin Sans', sans-serif", fontSize: label === 'Total' ? 15 : 12 }}>{val}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <StatusBadge status={order.status} />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
        </div>
      </div>

      {/* Item row */}
      <div style={{ display: 'flex', gap: 14, padding: '14px 18px', alignItems: 'center' }}>
        {item?.productSnapshot?.image
          ? <img src={item.productSnapshot.image} alt="" style={{ width: 54, height: 68, objectFit: 'cover', flexShrink: 0, border: '1px solid var(--border)' }} onError={e => e.target.style.display='none'} />
          : <div style={{ width: 54, height: 68, background: 'var(--cream-dark)', flexShrink: 0, border: '1px solid var(--border)' }} />
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontStyle: 'italic', color: 'var(--ink)', marginBottom: 3, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item?.productSnapshot?.name || 'Item'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            Size {item?.size} · {item?.rentalDurationLabel || item?.rentalDuration}
            {extra > 0 && <span style={{ color: 'var(--gold)', marginLeft: 8, fontWeight: 600 }}>+{extra} more item{extra > 1 ? 's' : ''}</span>}
          </div>
          {item?.returnDate && order.orderType !== 'purchase' && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
              Return by <strong style={{ color: isOverdue ? '#b71c1c' : isDueSoon ? '#b8860b' : 'var(--ink)' }}>{fmt(item.returnDate)}</strong>
              {isOverdue && <span style={{ marginLeft: 8, color: '#b71c1c', fontWeight: 700 }}>· Overdue</span>}
              {isDueSoon && !isOverdue && <span style={{ marginLeft: 8, color: '#b8860b', fontWeight: 700 }}>· Due soon</span>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {['Completed', 'Returned'].includes(order.status) && order.items?.[0]?.productId && (
            <button
              onClick={e => { e.stopPropagation(); navigate(`/product/${order.items[0].productId}`); }}
              style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', background: 'none', border: '1px solid var(--gold)', padding: '5px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Rent Again
            </button>
          )}
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)' }}>View →</div>
        </div>
      </div>
    </div>
  );
}

export function OrdersPage() {
  const navigate  = useNavigate();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('All');
  const [search,  setSearch]  = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await orderAPI.getMy({ limit: 50 });
      setOrders(data.orders || []);
    } catch { toast.error('Failed to load orders.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // College verification celebration — show once after verification
  useEffect(() => {
    try {
      if (localStorage.getItem('college_verified_celebrate') === 'true') {
        localStorage.removeItem('college_verified_celebrate');
        setTimeout(() => {
          toast.success(
            '🎓 College verified! +50 Warba Score points added. Unlimited orders unlocked.',
            { duration: 6000, style: { background: 'var(--ink)', color: 'var(--gold)', border: '1px solid var(--gold)', fontWeight: 600 } }
          );
        }, 800);
      }
    } catch {}
  }, []);

  const filtered = orders.filter(o => {
    const matchFilter = statusMatch(o.status, filter);
    const matchSearch = !search ||
      o.orderId?.toLowerCase().includes(search.toLowerCase()) ||
      o.items?.some(i => i.productSnapshot?.name?.toLowerCase().includes(search.toLowerCase()));
    return matchFilter && matchSearch;
  });

  if (loading) return <Spinner size="lg" centered />;

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ background: 'var(--ink)', padding: '28px clamp(16px,4vw,40px) 24px', borderBottom: '3px solid var(--gold)' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: 6 }}>Account</div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(26px,5vw,38px)', fontStyle: 'italic', fontWeight: 300, color: 'white', lineHeight: 1, marginBottom: 6 }}>My Orders</h1>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{orders.length} order{orders.length !== 1 ? 's' : ''} placed</p>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px clamp(16px,4vw,24px)' }}>
        {/* Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          <input
            style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border)', background: 'white', fontSize: 13, fontFamily: "'Josefin Sans', sans-serif", outline: 'none', boxSizing: 'border-box' }}
            placeholder="Search by order ID or item name..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
          <div style={{ display: 'flex', background: 'white', border: '1px solid var(--border)', alignSelf: 'flex-start', flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: '8px 16px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                background: filter === f ? 'var(--ink)' : 'none', color: filter === f ? 'var(--cream)' : 'var(--muted)',
                border: 'none', borderRight: '1px solid var(--border)', cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif",
              }}>{f}</button>
            ))}
          </div>
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', background: 'white', border: '1px solid var(--border)' }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontStyle: 'italic', fontWeight: 300, color: 'var(--cream-dark)', marginBottom: 8 }}>
              {orders.length === 0 ? 'No orders yet' : 'No orders match'}
            </div>
            <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 24 }}>
              {orders.length === 0 ? 'Your rental journey starts here.' : 'Try a different filter.'}
            </p>
            {orders.length === 0 && (
              <>
                {/* Quick category links */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 24 }}>
                  {['Blazers', 'Co-ords', 'Ethnic', 'Party', 'Casual'].map(cat => (
                    <button key={cat} onClick={() => navigate(`/browse?search=${cat}`)}
                      style={{ padding: '8px 16px', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'var(--cream)', border: '1px solid var(--border)', color: 'var(--ink)', cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.target.style.background = 'var(--ink)'; e.target.style.color = 'var(--cream)'; }}
                      onMouseLeave={e => { e.target.style.background = 'var(--cream)'; e.target.style.color = 'var(--ink)'; }}>
                      {cat}
                    </button>
                  ))}
                </div>
                <button onClick={() => navigate('/browse')} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', padding: '11px 28px', cursor: 'pointer' }}>
                  Browse Full Collection →
                </button>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map(order => <OrderRow key={order._id} order={order} />)}
          </div>
        )}
      </div>
    </div>
  );
}